<template>
  <Dialog class="layers-panel" v-model:visible="showSelectPanel" header="No Layers" :style="{ width: '20rem', position: 'absolute', top: '120px', left: '20px' }">
    <template #header>
      <div v-if="elements.length">
        <IconPreviewOpen class="handler-item" style="font-weight: bold;margin-right: 5px;font-size: 17px;" @click="showAll()"/>
        <IconPreviewClose class="handler-item" style="font-size: 17px;" @click="hideAll()" />
      </div>
    </template>

    <div class="element-list">
      <template v-for="item in elements" :key="item.id">
        <div class="group-els" v-if="item.type === 'group'">
          <div class="group-title">Group</div>
          <div
              class="item"
              :class="{
              'active': activeElementIdList.includes(groupItem.id),
              'group-active': activeGroupElementId.includes(groupItem.id),
            }"
              v-for="groupItem in item.elements"
              :key="groupItem.id"
              @click="selectGroupEl(item, groupItem.id)"
              @dblclick="enterEdit(groupItem.id)"
          >
            <input
                :id="`input-${groupItem.id}`"
                :value="groupItem.name || ELEMENT_TYPE_ZH[groupItem.type]"
                class="input"
                type="text"
                v-if="editingElId === groupItem.id"
                @blur="$event => saveElementName($event, groupItem.id)"
                @keydown.enter="$event => saveElementName($event, groupItem.id)"
            >
            <div v-else class="name">{{groupItem.name || ELEMENT_TYPE_ZH[groupItem.type]}}</div>
            <div class="icons">
              <IconPreviewClose style="font-size: 17px;" @click.stop="hideElement(groupItem.id)" v-if="hiddenElementIdList.includes(groupItem.id)" />
              <IconPreviewOpen style="font-size: 17px;" @click.stop="hideElement(groupItem.id)" v-else />
            </div>
          </div>
        </div>
        <div
            class="item"
            :class="{ 'active': activeElementIdList.includes(item.id) }"
            v-else
            @click="selectEl(item.id)"
            @dblclick="enterEdit(item.id)"
        >
          <input
              :id="`input-${item.id}`"
              :value="item.name || ELEMENT_TYPE_ZH[item.type]"
              class="input"
              type="text"
              v-if="editingElId === item.id"
              @blur="$event => saveElementName($event, item.id)"
              @keydown.enter="$event => saveElementName($event, item.id)"
          >
          <div v-else class="name">{{item.name || ELEMENT_TYPE_ZH[item.type]}}</div>
          <div class="icons">
            <IconDelete v-if="activeElementIdList.find(el => el === item.id)" style="padding-right: 10px;font-size: 15px;" @click.stop="deleteElement()" />
            <IconPreviewClose style="font-size: 17px;" @click.stop="hideElement(item.id)" v-if="hiddenElementIdList.includes(item.id)" />
            <IconPreviewOpen style="font-size: 17px;" @click.stop="hideElement(item.id)" v-else />
          </div>
        </div>
      </template>
    </div>

    <template #footer>
      <div v-if="elements.length">
        <IconDown class="handler-item"  @click="orderElement(handleElement!, ElementOrderCommands.DOWN)" />
        <IconUp class="handler-item"  @click="orderElement(handleElement!, ElementOrderCommands.UP)" />
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useSlidesStore, useMainStore } from '@/store'
import { PPTElement } from '@/types/slides'
import { ELEMENT_TYPE_ZH } from '@/configs/element'
import useOrderElement from '@/hooks/useOrderElement'
import { ElementOrderCommands } from '@/types/edit'

import { Input } from 'ant-design-vue'
import useDeleteElement from "@/hooks/useDeleteElement.ts";

const slidesStore = useSlidesStore()
const mainStore = useMainStore()
const { currentSlide } = storeToRefs(slidesStore)
const { handleElement, handleElementId, activeElementIdList, activeGroupElementId, hiddenElementIdList, showSelectPanel } = storeToRefs(mainStore)

const { deleteElement } = useDeleteElement()
const { orderElement } = useOrderElement()

interface GroupElements {
  type: 'group'
  id: string
  elements: PPTElement[]
}
type ElementItem = PPTElement | GroupElements

const elements = computed<ElementItem[]>(() => {
  const _elements: ElementItem[] = []

  for (let index = currentSlide.value.elements.length - 1; index >= 0; index--) {
    const el = currentSlide.value.elements[index]
    if (el.type !== 'dragdrop' && (el.type !== 'slidelayout' || !el.canvasHide) ) {
      if (el.groupId) {
        const lastItem = _elements[_elements.length - 1]

        if (lastItem && lastItem.type === 'group' && lastItem.id && lastItem.id === el.groupId) {
          lastItem.elements.push(el)
        }
        else _elements.push({ type: 'group', id: el.groupId, elements: [el] })
      }
      else _elements.push(el)
    }
  }

  return _elements
})

const selectGroupEl = (item: GroupElements, id: string) => {
  if (handleElementId.value === id) return
  if (hiddenElementIdList.value.includes(id)) return

  const idList = item.elements.map(el => el.id)
  mainStore.setActiveElementIdList(idList)
  mainStore.setHandleElementId(id)
  nextTick(() => mainStore.setActiveGroupElementId(id))
}

const selectEl = (id: string) => {
  if (handleElementId.value === id) return
  if (hiddenElementIdList.value.includes(id)) return

  mainStore.setActiveElementIdList([id])
}

const hideElement = (id: string) => {
  if (hiddenElementIdList.value.includes(id)) {
    mainStore.setHiddenElementIdList(hiddenElementIdList.value.filter(item => item !== id))
  }
  else mainStore.setHiddenElementIdList([...hiddenElementIdList.value, id])

  if (activeElementIdList.value.includes(id)) mainStore.setActiveElementIdList([])
}

const showAll = () => {
  const currentSlideElIdList = currentSlide.value.elements.map(item => item.id)
  const needHiddenElementIdList = hiddenElementIdList.value.filter(item => !currentSlideElIdList.includes(item))
  mainStore.setHiddenElementIdList(needHiddenElementIdList)
}
const hideAll = () => {
  const currentSlideElIdList = currentSlide.value.elements.map(item => item.id)
  mainStore.setHiddenElementIdList([...hiddenElementIdList.value, ...currentSlideElIdList])
  if (activeElementIdList.value.length) mainStore.setActiveElementIdList([])
}

const editingElId = ref('')

const saveElementName = (e: FocusEvent | KeyboardEvent, id: string) => {
  const name = (e.target as HTMLInputElement).value
  slidesStore.updateElement({ id, props: { name } })
  editingElId.value = ''
}

const enterEdit = (id: string) => {
  editingElId.value = id
  nextTick(() => {
    const inputRef = document.querySelector(`#input-${id}`) as HTMLInputElement
    inputRef.focus()
  })
}

const close = () => {
  mainStore.setSelectPanelState(false)
}
</script>

<style lang="scss" scoped>
.select-panel {
  height: 100%;
  font-size: 12px;
  user-select: none;
}

.layers-panel {
  top: 120px;
  left: 20px;
}

.element-list {
  height: calc(100% - 50px);
  padding-right: 10px;
  margin-right: -10px;
  overflow: auto;
}
.item {
  padding: 5px;
  font-size: 12px;
  border-radius: $borderRadius;
  display: flex;
  align-items: center;
  cursor: pointer;

  &.active {
    background-color: rgba(202, 202, 202, 0.1);
  }
  &.group-active {
    background-color: rgba(202, 202, 202, .2);
  }
  &:hover {
    background-color: rgba(202, 202, 202, .25);
  }

  .name {
    height: 18px;
    line-height: 18px;
    flex: 1;
    @include ellipsis-oneline();
  }
  .icons {
    width: 40px;
    display: flex;
    align-items: flex-end;
    justify-content: right;
    margin-left: 5px;
  }
}
.group-els {
  padding: 5px 0;

  .group-title {
    margin-bottom: 5px;
    padding: 0 5px;
  }
  .item {
    margin-left: 15px;
  }
}
.input {
  width: 100%;
  height: 18px;
  line-height: 18px;
  border: 0;
  outline: 0;
  padding-left: 0;
  padding-right: 0;
  flex: 1;
}
</style>
